import { useState, useEffect } from "react";
import { loadStyles } from "../../../utils/cssLoader";
import { truncateText, formatRssDate } from "../../../utils/interactivesHelper";
import NextImage from "../../common/NextImage";

/**
 * YoutubeCard — renders a YouTube thumbnail with a play button.
 * Clicking play swaps the thumbnail for the live iframe embed (autoplay).
 * Clicking the card itself (outside the play button) opens the FeedSlider.
 */
const YoutubeCard = ({ item = {}, loadCSS = true, onClick = null }) => {
  const { title, description, pubDate, image, embedUrl, author, authorImage } = item;
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (loadCSS === true || loadCSS === "true") {
      loadStyles("interactives");
    }
  }, [loadCSS]);

  const displayTitle = title || truncateText(description, 80);
  if (!displayTitle && !image && !embedUrl) return null;

  // Append autoplay=1 to the embed URL when the user clicks play
  const autoplayUrl = embedUrl
    ? `${embedUrl}${embedUrl.includes("?") ? "&" : "?"}autoplay=1`
    : null;

  const handlePlay = (e) => {
    e.stopPropagation(); // don't open the slider
    setPlaying(true);
  };

  return (
    <div
      className="yt-card"
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      {/* Media area: thumbnail → iframe swap */}
      <div className="yt-card__media">
        {playing && autoplayUrl ? (
          <iframe
            className="yt-card__iframe"
            src={autoplayUrl}
            title={displayTitle}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            {image ? (
              <NextImage
                src={image}
                alt={displayTitle}
                clz="yt-card__thumb"
                fill
                objectFit="cover"
              />
            ) : (
              <div className="yt-card__thumb-placeholder" />
            )}

            {/* YouTube red play button */}
            {embedUrl && (
              <button
                className="yt-card__play-btn"
                onClick={handlePlay}
                aria-label="Play video"
              >
                <span className="yt-card__play-icon">▶</span>
              </button>
            )}
          </>
        )}
      </div>

      {/* Body */}
      <div className="yt-card__body">
        {/* Author row */}
        {author && (
          <div className="yt-card__author">
            {authorImage ? (
              <NextImage
                src={authorImage}
                alt={author}
                clz="yt-card__author-img"
                width={20}
                height={20}
              />
            ) : (
              <span className="yt-card__yt-logo">▶</span>
            )}
            <span className="yt-card__author-name">{author}</span>
          </div>
        )}

        {displayTitle && (
          <span className="yt-card__title">{displayTitle}</span>
        )}

        {pubDate && (
          <span className="yt-card__date">{formatRssDate(pubDate)}</span>
        )}
      </div>
    </div>
  );
};

export default YoutubeCard;
